import { Checkbox } from '@src/components/ui/checkbox';
import { CheckboxSingleFlow } from '@src/components/ui/checkbox-flow';
import { SwitchFlow } from '@src/components/ui/switch-flow';

import useLangText from '@shared/hooks/useLangText';
import {
    ProcessFieldBase,
    type ProcessFieldCheckbox as ProcessFieldCheckboxType,
} from '@shared/types/process';

import { ShadcnForm } from '@shadcn-features/core';
import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

interface Props extends React.ComponentPropsWithoutRef<typeof ShadcnForm.Item> {
    field: ProcessFieldBase & ProcessFieldCheckboxType;
    initialValue?: boolean | null;
    fieldProps?: Omit<
        React.ComponentPropsWithoutRef<typeof Checkbox>,
        | 'onCheckedChange'
        | 'onChange'
        | 'checked'
        | 'defaultChecked'
        | 'defaultValue'
        | 'value'
        | 'ref'
    >;
}

export function ProcessFieldCheckboxField({
    field,
    fieldProps,
    initialValue,
    ...props
}: Props) {
    const getLangText = useLangText();
    const form = useFormContext();
    const fieldLabel = getLangText(field?.name ?? '');
    const itemProps = { ...props };
    delete itemProps.label;
    delete itemProps.tooltip;
    const defaultValue = getCheckboxDefaultValue(
        initialValue,
        field.defaultValue,
    );

    useEffect(() => {
        if (form.getValues(props.name) == null) {
            form.setValue(props.name, defaultValue);
        }
    }, [defaultValue, form, props.name]);

    const getValueFromEvent = (checked: unknown) => {
        if (typeof checked === 'boolean') return checked;
        return checked;
    };

    return (
        <ShadcnForm.Item
            {...itemProps}
            defaultValue={defaultValue}
            getValueFromEvent={getValueFromEvent}
            valuePropName={'checked'}
        >
            {field?.options?.displayType === 'switch' ? (
                <SwitchFlow
                    readOnly={props.readOnly}
                    subTitle={getLangText(field?.description ?? '')}
                    title={fieldLabel}
                    {...fieldProps}
                />
            ) : (
                <CheckboxSingleFlow
                    readOnly={props.readOnly}
                    subTitle={fieldLabel}
                    {...fieldProps}
                />
            )}
        </ShadcnForm.Item>
    );
}

function getCheckboxDefaultValue(
    initialValue: boolean | null | undefined,
    fieldDefaultValue: ProcessFieldBase['defaultValue'],
): boolean {
    if (typeof initialValue === 'boolean') return initialValue;
    if (initialValue === null) return false;
    if (!fieldDefaultValue) return false;

    try {
        const parsedValue = JSON.parse(fieldDefaultValue);
        return typeof parsedValue === 'boolean' ? parsedValue : false;
    } catch {
        return false;
    }
}
